import { FileText, TrendingUp, Users, MessageSquare, Search, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export const ProcessSteps = () => {
  const steps = [
    {
      number: '01',
      title: 'Unterlagen & Vorbereitung',
      description:
        'Zusammenstellung aller relevanten Dokumente und Objektdaten.',
      icon: FileText,
      link: '/ablauf/phase-1-unterlagen'
    },
    {
      number: '02',
      title: 'Bewertung & Positionierung',
      description:
        'Professionelle Marktanalyse und Festlegung der Verkaufsstrategie.',
      icon: TrendingUp,
      link: '/ablauf/phase-2-bewertung'
    },
    {
      number: '03',
      title: 'Käuferansprache',
      description:
        'Gezielte Ansprache passender Käufergruppen bundesweit.',
      icon: Users,
      link: '/ablauf/phase-3-vermarktung'
    },
    {
      number: '04',
      title: 'Verhandlung',
      description:
        'Professionelle Verhandlungsführung und Angebotsprüfung.',
      icon: MessageSquare,
      link: '/ablauf/phase-4-verhandlung'
    },
    {
      number: '05',
      title: 'Notartermin',
      description:
        'Vertragsabstimmung und notarielle Beurkundung.',
      icon: Search,
      link: '/ablauf/phase-5-notar'
    },
    {
      number: '06',
      title: 'Übergabe',
      description:
        'Sichere Abwicklung und Begleitung bis zum erfolgreichen Abschluss.',
      icon: CheckCircle2,
      link: '/ablauf/phase-6-uebergabe'
    },
  ];

  return (
    <section 
      className="py-24 relative overflow-hidden" 
      data-testid="process-section"
      style={{
        background: 'linear-gradient(to bottom, #EFF6FF 0%, #FFFFFF 50%, #EFF6FF 100%)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-block">
            <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ color: 'var(--navy-900)' }}
          >
            So läuft der Verkauf ab
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
            Unser strukturierter 6-Schritte-Prozess für Ihren Erfolg
          </p>
        </div>

        <div className="relative">
          {/* Desktop Timeline - Alternating Layout */}
          <div className="hidden lg:block">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`relative flex items-center mb-16 ${
                    isEven ? 'flex-row' : 'flex-row-reverse'
                  }`}
                  data-testid={`process-step-${index}`}
                >
                  {/* Content Side */}
                  <div className={`w-5/12 ${isEven ? 'text-right pr-12' : 'text-left pl-12'}`}>
                    <Link to={step.link}>
                      <div className="glass-card p-6 rounded-xl inline-block hover:shadow-2xl transition-shadow cursor-pointer">
                        <h3
                          className="text-2xl font-bold mb-3"
                          style={{ color: 'var(--navy-800)' }}
                        >
                          {step.title}
                        </h3>
                        <p className="text-base leading-relaxed" style={{ color: 'var(--gray-600)' }}>
                          {step.description}
                        </p>
                      </div>
                    </Link>
                  </div>

                  {/* Center - Large Number Circle */}
                  <div className="w-2/12 flex justify-center relative z-10">
                    <div
                      className="w-28 h-28 rounded-full flex items-center justify-center shadow-2xl relative"
                      style={{
                        background: index % 2 === 0 
                          ? 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)'
                          : 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)'
                      }}
                    >
                      <span 
                        className="timeline-number text-5xl text-white"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                      >
                        {step.number}
                      </span>
                      
                      {/* Connection Line */}
                      {index < steps.length - 1 && (
                        <div
                          className="absolute top-full w-1 h-16"
                          style={{
                            background: 'linear-gradient(to bottom, rgba(212, 168, 83, 0.5), rgba(37, 99, 160, 0.3))'
                          }}
                        ></div>
                      )}
                    </div>
                  </div>

                  {/* Icon Side */}
                  <div className={`w-5/12 ${isEven ? 'pl-12' : 'pr-12'}`}>
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--navy-100)' }}
                    >
                      <IconComponent
                        className="h-10 w-10"
                        style={{ color: 'var(--navy-600)' }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Timeline - Vertical */}
          <div className="lg:hidden space-y-8">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              
              return (
                <div
                  key={index}
                  className="relative flex gap-6"
                  data-testid={`process-step-mobile-${index}`}
                >
                  {/* Left Side - Number + Line */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg flex-shrink-0"
                      style={{
                        background: index % 2 === 0 
                          ? 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)'
                          : 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)'
                      }}
                    >
                      <span className="timeline-number text-3xl text-white">
                        {step.number}
                      </span>
                    </div>
                    
                    {index < steps.length - 1 && (
                      <div
                        className="w-1 h-full mt-4"
                        style={{
                          background: 'linear-gradient(to bottom, rgba(212, 168, 83, 0.3), rgba(37, 99, 160, 0.2))',
                          minHeight: '60px'
                        }}
                      ></div>
                    )}
                  </div>

                  {/* Right Side - Content */}
                  <div className="flex-1 pb-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: 'var(--navy-100)' }}
                      >
                        <IconComponent
                          className="h-6 w-6"
                          style={{ color: 'var(--navy-600)' }}
                        />
                      </div>
                      <h3
                        className="text-xl font-bold"
                        style={{ color: 'var(--navy-800)' }}
                      >
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--gray-600)' }}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Button nach Timeline */}
        <div className="text-center mt-12">
          <Link to="/ablauf">
            <Button
              size="lg"
              className="btn-gold-gradient text-white border-0 font-semibold px-10 py-6 text-lg shadow-lg"
            >
              Gesamten Verkaufsprozess ansehen
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
